/**
 * Shared cart / checkout pricing helpers so side cart, checkout, payment, and success stay consistent.
 */

export function sumCartSubtotal(cart) {
  return (cart || []).reduce(
    (acc, item) => acc + Number(item?.qty || 0) * Number(item?.discountPrice || 0),
    0
  );
}

export function sumPostageFees(cart) {
  return (cart || []).reduce(
    (acc, item) =>
      acc +
      Number(item?.qty || 0) *
        Number(item?.postageFees ?? item?.postageFee ?? item?.shippingFee ?? 0),
    0
  );
}

/** True when postage is not stored on the cart line (needs product fetch). */
export function cartLineMissingPostage(item) {
  const raw = item?.postageFees ?? item?.postageFee ?? item?.shippingFee;
  return raw === undefined || raw === null;
}

export function resolveCartLinePostageFee(item, postageByProductId = {}) {
  const raw = item?.postageFees ?? item?.postageFee ?? item?.shippingFee;
  if (raw !== undefined && raw !== null) return Number(raw);
  const id = String(item?._id || item?.productId || item?.id || "");
  if (id && postageByProductId[id] !== undefined) {
    return Number(postageByProductId[id]);
  }
  return 0;
}

export function enrichCartWithPostageFees(cart, postageByProductId = {}) {
  return (cart || []).map((item) => {
    if (!cartLineMissingPostage(item)) return item;
    const id = String(item?._id || item?.productId || item?.id || "");
    if (id && postageByProductId[id] !== undefined) {
      return { ...item, postageFees: Number(postageByProductId[id]) };
    }
    return item;
  });
}

export function computeBuyerProtectionFee(subTotal, config = {}) {
  const enabled = config.enabled !== false;
  if (!enabled) return 0;
  const fixed = Number(config.fixedFee ?? 0.7);
  const pct = Number(config.percentage ?? 2) / 100;
  return Number((fixed + Number(subTotal || 0) * pct).toFixed(2));
}

/**
 * Total the buyer should pay from checkout / payment / success payload fields.
 * Matches cart + checkout: subtotal + postage charged + buyer protection − bundle − code/veteran discounts.
 * When `freeShipping` is true, postage is treated as 0 for the total (fees may still show for reference).
 */
export function computeBuyerFacingTotalFromCheckoutPayload(payload) {
  if (!payload || typeof payload !== "object") return 0;
  const sub = Number(payload.subTotalPrice ?? 0);
  const postageStored = Number(payload.postageFees ?? 0);
  const shippingLegacy = Number(payload.shipping ?? 0);
  const postageDisplay = postageStored > 0 ? postageStored : shippingLegacy;
  const postageCharged = payload.freeShipping ? 0 : postageDisplay;
  const bpf = Number(payload.buyerProtectionFee ?? 0);
  const bundle = Number(payload.bundleDiscount ?? 0);
  const bundlePostage = Number(payload.bundlePostageDiscount ?? 0);
  const disc = Number(payload.discountPrice ?? 0);
  const postageAfterBundle = Math.max(0, postageCharged - bundlePostage);
  return Math.max(
    0,
    Number((sub + postageAfterBundle + bpf - bundle - disc).toFixed(2))
  );
}

export function postageChargedFromCheckoutPayload(payload) {
  if (!payload || typeof payload !== "object") return 0;
  const postageStored = Number(payload.postageFees ?? 0);
  const shippingLegacy = Number(payload.shipping ?? 0);
  const postageDisplay = postageStored > 0 ? postageStored : shippingLegacy;
  const base = payload.freeShipping ? 0 : postageDisplay;
  const bundlePostage = Number(payload.bundlePostageDiscount ?? 0);
  return Math.max(0, Number((base - bundlePostage).toFixed(2)));
}

export function bundlePostageDiscountLabel(percentage) {
  const pct = Number(percentage);
  if (!Number.isFinite(pct) || pct <= 0) return "Bundle postage discount";
  return `Bundle postage discount`;
}

export function isBundlePostagePromoEnabled(config) {
  if (!config || config.enabled === false) return false;
  return Number(config.percentage ?? 0) > 0;
}

export function bundlePostagePromoBadge(percentage) {
  const pct = Number(percentage);
  if (!Number.isFinite(pct) || pct <= 0) return "";
  return `${pct}% off postage`;
}

export function bundlePostagePromoSentence(percentage) {
  const pct = Number(percentage);
  if (!Number.isFinite(pct) || pct <= 0) return "";
  return `Qualifying bundles also save ${pct}% off total postage at checkout.`;
}

/** Collapse duplicated / multiline product titles from legacy cart data. */
export function normalizeProductDisplayName(raw) {
  let s = String(raw || "").trim();
  if (!s) return "Product";
  const lines = s.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2 && lines.every((l) => l === lines[0])) return lines[0];
  if (lines.length > 1) return lines[0];
  return s.replace(/\s+/g, " ");
}
